import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

export default function MachineLearningPage() {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-white pt-32 pb-20">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-purple-400/20 to-blue-400/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-semibold rounded-full mb-6">
                AI SOLUTIONS
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Machine Learning
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Solutions
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Transform your data into actionable insights with cutting-edge machine learning algorithms. Build predictive models that learn, adapt, and improve over time.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="/contact" className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Get Started
                </a>
                <a href="/case-studies" className="px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg font-semibold hover:border-purple-500 transition-all duration-300 whitespace-nowrap cursor-pointer">
                  View Case Studies
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                CAPABILITIES
              </div>
              <h2 className="text-4xl font-bold mb-4">What We Offer</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Comprehensive machine learning solutions tailored to your business needs
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-brain-line',
                  title: 'Predictive Analytics',
                  description: 'Forecast trends, customer behavior, and business outcomes with advanced predictive models.',
                  gradient: 'from-blue-500 to-cyan-500'
                },
                {
                  icon: 'ri-eye-line',
                  title: 'Computer Vision',
                  description: 'Image recognition, object detection, and visual analysis for automated decision-making.',
                  gradient: 'from-purple-500 to-pink-500'
                },
                {
                  icon: 'ri-message-3-line',
                  title: 'Natural Language Processing',
                  description: 'Extract meaning from text, automate content analysis, and enable intelligent conversations.',
                  gradient: 'from-indigo-500 to-blue-500'
                },
                {
                  icon: 'ri-line-chart-line',
                  title: 'Time Series Analysis',
                  description: 'Analyze temporal data patterns for forecasting, anomaly detection, and trend analysis.',
                  gradient: 'from-violet-500 to-purple-500'
                },
                {
                  icon: 'ri-group-line',
                  title: 'Clustering & Segmentation',
                  description: 'Discover hidden patterns and group similar data points for better insights.',
                  gradient: 'from-blue-500 to-indigo-500'
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Anomaly Detection',
                  description: 'Identify unusual patterns and outliers in real-time to prevent fraud and errors.',
                  gradient: 'from-purple-500 to-violet-500'
                }
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="group bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                >
                  <div className={`w-14 h-14 flex items-center justify-center bg-gradient-to-br ${feature.gradient} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                OUR PROCESS
              </div>
              <h2 className="text-4xl font-bold mb-4">How We Build ML Solutions</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                A proven methodology for delivering high-performance machine learning models
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                {
                  step: '01',
                  title: 'Data Collection',
                  description: 'Gather and prepare quality data from multiple sources',
                  icon: 'ri-database-2-line'
                },
                {
                  step: '02',
                  title: 'Model Training',
                  description: 'Train algorithms using advanced techniques and frameworks',
                  icon: 'ri-settings-3-line'
                },
                {
                  step: '03',
                  title: 'Validation',
                  description: 'Test and validate model accuracy and performance',
                  icon: 'ri-checkbox-circle-line'
                },
                {
                  step: '04',
                  title: 'Deployment',
                  description: 'Deploy models to production with monitoring',
                  icon: 'ri-rocket-line'
                }
              ].map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300">
                    <div className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                      {step.step}
                    </div>
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mb-4">
                      <i className={`${step.icon} text-xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                TECHNOLOGY STACK
              </div>
              <h2 className="text-4xl font-bold mb-4">Tools We Use</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Industry-leading frameworks and platforms for building robust ML solutions
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {['TensorFlow', 'PyTorch', 'Scikit-learn', 'Keras', 'XGBoost', 'Apache Spark', 'Pandas', 'NumPy'].map((tech, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="text-lg font-semibold text-gray-800">{tech}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">
              Ready to Leverage
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Machine Learning?
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Let's discuss how our ML solutions can transform your business operations and drive growth.
            </p>
            <a 
              href="/contact"
              className="inline-block px-10 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Schedule a Consultation
            </a>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}